/* ========================================================================== 
   R2-backed uploads for Shopify contact forms.

   A selected file is uploaded before the native Shopify contact form posts.
   The private download URL returned by the Worker is then included in the
   contact notification as contact[proof_url].
   ========================================================================== */
(function () {
  'use strict';

  /* ===== THIS FILE IS FOR THE kimdyma STORE (kimdyma.com) ===== */
  var ENDPOINT = 'https://uploads.kimdyma.com/upload';
  var TURNSTILE_SITE_KEY = '0x4AAAAAAD4Aa1eWWzQLiJel';
  var TURNSTILE_ACTION = 'r2_upload';
  var MAX_FILE_SIZE = 20 * 1024 * 1024;
  var FORM_SELECTOR = 'form.eb-r2-upload-form';

  function setStatus(status, state, message) {
    if (!status) return;
    status.hidden = !message;
    status.classList.remove('is-uploading', 'is-success', 'is-error');
    if (state) status.classList.add('is-' + state);
    status.textContent = message || '';
  }

  function ensureStatus(form, submitButton) {
    var status = form.querySelector('[data-r2-upload-status]');
    if (status) return status;

    status = document.createElement('p');
    status.className = 'eb-r2-upload-status';
    status.setAttribute('data-r2-upload-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;

    var actions = submitButton && submitButton.parentElement;
    if (actions && actions.parentElement) {
      actions.parentElement.insertBefore(status, actions);
    } else {
      form.appendChild(status);
    }
    return status;
  }

  function ensureTurnstileContainer(form, status) {
    var container = form.querySelector('[data-eb-turnstile]');
    if (container) return container;

    container = document.createElement('div');
    container.className = 'eb-r2-turnstile';
    container.setAttribute('data-eb-turnstile', '');

    if (status && status.parentElement) {
      status.parentElement.insertBefore(container, status);
    } else {
      form.appendChild(container);
    }
    return container;
  }

  function hiddenField(form, name, value) {
    var input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function clearUploadResult(form) {
    form.removeAttribute('data-r2-uploaded');
    ['contact[proof_url]', 'contact[proof_file_name]', 'contact[proof_r2_key]'].forEach(function (name) {
      var field = form.querySelector('input[name="' + name + '"]');
      if (field) field.remove();
    });
  }

  function fileInputIsActive(input) {
    return !input.disabled && !input.closest('[hidden]');
  }

  function message(config, key, fallback) {
    return (config && config.getAttribute('data-' + key)) || fallback;
  }

  function resetTurnstile(state) {
    state.token = '';
    state.pending = false;
    state.pendingSubmitter = null;

    if (window.turnstile && state.widgetId !== null) {
      try {
        window.turnstile.reset(state.widgetId);
      } catch (_error) {
        /* The widget may have been removed by Shopify's theme editor. */
      }
    }
  }

  function renderTurnstile(state) {
    if (state.widgetId !== null) return true;
    if (!window.turnstile || typeof window.turnstile.render !== 'function') return false;

    try {
      state.widgetId = window.turnstile.render(state.container, {
        sitekey: TURNSTILE_SITE_KEY,
        action: TURNSTILE_ACTION,
        /* Run on page load, not on submit: the token is then already there when
           the customer presses the button, instead of a challenge popping up
           after they think they are done. Still invisible unless Cloudflare
           actually wants an interaction. */
        appearance: 'interaction-only',
        size: 'flexible',
        theme: 'auto',
        'response-field': false,
        callback: function (token) {
          state.token = token;

          if (state.pending) {
            var submitter = state.pendingSubmitter;
            state.pending = false;
            state.pendingSubmitter = null;
            window.setTimeout(function () {
              state.form.requestSubmit(submitter || undefined);
            }, 0);
          }
        },
        'expired-callback': function () {
          state.token = '';
          if (state.pending) {
            state.pending = false;
            state.pendingSubmitter = null;
            setStatus(
              state.status,
              'error',
              message(state.config, 'verification-failed', 'Security verification expired. Please try again.')
            );
          }
        },
        'timeout-callback': function () {
          state.token = '';
          state.pending = false;
          state.pendingSubmitter = null;
          setStatus(
            state.status,
            'error',
            message(state.config, 'verification-failed', 'Security verification timed out. Please try again.')
          );
        },
        'error-callback': function () {
          state.token = '';
          state.pending = false;
          state.pendingSubmitter = null;
          setStatus(
            state.status,
            'error',
            message(state.config, 'verification-failed', 'Security verification failed. Please try again.')
          );
          window.setTimeout(function () { resetTurnstile(state); }, 0);
        }
      });
      return true;
    } catch (_error) {
      state.widgetId = null;
      return false;
    }
  }

  function initForm(form) {
    if (form.getAttribute('data-r2-initialized') === 'true') return;

    var fileInput = form.querySelector('[data-eb-upload-input]');
    if (!fileInput) return;

    var config = form.querySelector('[data-eb-upload-config]');

    form.setAttribute('data-r2-initialized', 'true');

    var submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    var status = ensureStatus(form, submitButton);
    var turnstileState = {
      form: form,
      config: config,
      status: status,
      container: ensureTurnstileContainer(form, status),
      widgetId: null,
      token: '',
      pending: false,
      pendingSubmitter: null
    };
    var uploading = false;

    form.ebTurnstileState = turnstileState;
    renderTurnstile(turnstileState);

    /* Validate as soon as a file is chosen so an oversized or unsupported file
       can never reach submit. The form stays blocked until it is replaced. */
    var fileInvalid = false;

    function acceptedTypes() {
      return String(fileInput.getAttribute('accept') || '')
        .split(',')
        .map(function (value) { return value.trim().toLowerCase(); })
        .filter(Boolean);
    }

    function typeIsAccepted(file) {
      var accepted = acceptedTypes();
      if (!accepted.length) return true;

      var mime = String(file.type || '').toLowerCase();
      var name = String(file.name || '').toLowerCase();

      return accepted.some(function (rule) {
        if (rule.charAt(0) === '.') return name.slice(-rule.length) === rule;
        if (rule.slice(-2) === '/*') return mime.indexOf(rule.slice(0, -1)) === 0;
        return mime === rule;
      });
    }

    function validateFile() {
      fileInvalid = false;
      if (!fileInputIsActive(fileInput) || !fileInput.files || !fileInput.files.length) {
        setStatus(status, '', '');
        if (submitButton) submitButton.disabled = false;
        return;
      }

      var chosen = fileInput.files[0];
      var problem = '';

      if (chosen.size > MAX_FILE_SIZE) {
        problem = message(config, 'too-large', 'The file must be 20 MB or smaller. Please choose another file.');
      } else if (!typeIsAccepted(chosen)) {
        problem = message(config, 'wrong-type', 'That file type is not supported. Please choose an image or PDF.');
      }

      if (problem) {
        fileInvalid = true;
        setStatus(status, 'error', problem);
        if (submitButton) submitButton.disabled = true;
        status.focus && status.focus();
        return;
      }

      setStatus(status, '', '');
      if (submitButton) submitButton.disabled = false;
    }

    fileInput.addEventListener('change', function () {
      clearUploadResult(form);
      validateFile();
    });

    function collectFields(payload) {
      var els = form.querySelectorAll('input[name^="contact["], select[name^="contact["], textarea[name^="contact["]');
      Array.prototype.forEach.call(els, function (el) {
        var name = el.name;
        if (el === fileInput) return;
        if (name === 'contact[botfield]' || name === 'contact[honey]') return; /* honeypot */
        if (name === 'contact[proof_url]' || name === 'contact[proof_file_name]' || name === 'contact[proof_r2_key]') return;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        payload.append(name, el.value);
      });
    }

    form.addEventListener('submit', function (event) {
      if (form.getAttribute('data-r2-uploaded') === 'true') return;

      /* File is optional. Without one we still send the fields to the Worker so
         the submission is recorded in NocoDB; with one we also upload to R2. */
      var hasFile = fileInputIsActive(fileInput) && fileInput.files && fileInput.files.length > 0;

      event.preventDefault();
      if (uploading) return;

      /* Re-check here too: the submit must never go through with a bad file. */
      if (hasFile) validateFile();
      if (fileInvalid) {
        status.focus && status.focus();
        return;
      }

      var file = hasFile ? fileInput.files[0] : null;

      var submitter = event.submitter || submitButton;
      if (!turnstileState.token) {
        turnstileState.pending = true;
        turnstileState.pendingSubmitter = submitter;

        if (!renderTurnstile(turnstileState)) {
          turnstileState.pending = false;
          turnstileState.pendingSubmitter = null;

          /* No attachment: don't block a plain enquiry if Turnstile can't load —
             let Shopify's native form (email) proceed. Only file uploads, which
             the Worker gates on Turnstile, must hard-fail here.
             This path skips the Worker, so the row never reaches NocoDB — say so
             loudly, otherwise it looks like a successful submission. */
          if (!hasFile) {
            console.warn('[r2-upload] Turnstile unavailable — submitted by email only, NOT recorded in NocoDB.');
            form.setAttribute('data-r2-uploaded', 'true');
            form.requestSubmit(submitter || undefined);
            return;
          }

          setStatus(
            status,
            'error',
            message(config, 'verification-unavailable', 'Security verification could not load. Please refresh and try again.')
          );
          return;
        }

        /* The widget runs on page load, so normally a token is already here.
           Getting this far means Cloudflare wants an interaction: the checkbox
           is on screen and its callback re-submits the form once solved. */
        setStatus(status, 'uploading', message(config, 'verifying', 'Please complete the security check above, then we will send your form…'));
        try {
          if (typeof window.turnstile.execute === 'function') {
            window.turnstile.execute(turnstileState.widgetId);
          }
        } catch (_error) {
          /* execute() only applies to execute-mode widgets; ignore and let the
             visible checkbox drive the callback. */
        }
        return;
      }

      var nameInput = form.querySelector('[name="contact[name]"]');
      var customerName = nameInput ? nameInput.value.trim() : '';
      var category = (config && config.getAttribute('data-category')) || 'warranty';
      var payload = new FormData();
      var controller = new AbortController();
      var timer = window.setTimeout(function () { controller.abort(); }, 90000);

      if (hasFile) payload.append('file', file, file.name);
      payload.append('category', category);
      payload.append('customerName', customerName || 'Customer');
      collectFields(payload);

      uploading = true;
      form.classList.add('is-r2-uploading');
      if (submitButton) submitButton.disabled = true;
      setStatus(
        status,
        'uploading',
        hasFile
          ? message(config, 'uploading', 'Uploading your file securely…')
          : message(config, 'sending', 'Sending your form…')
      );

      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'X-Turnstile-Token': turnstileState.token },
        body: payload,
        signal: controller.signal
      })
        .then(function (response) {
          return response.json().catch(function () { return {}; }).then(function (body) {
            if (!response.ok || !body.ok) {
              var uploadError = new Error(body.error || 'Submission failed');
              uploadError.code = body.code || '';
              throw uploadError;
            }
            return body;
          });
        })
        .then(function (result) {
          if (result.downloadUrl) {
            hiddenField(form, 'contact[proof_url]', result.downloadUrl);
            hiddenField(form, 'contact[proof_file_name]', result.fileName || (file && file.name) || '');
            hiddenField(form, 'contact[proof_r2_key]', result.key || '');
            /* Never ask Shopify's native contact endpoint to process the file. */
            fileInput.disabled = true;
          }
          form.setAttribute('data-r2-uploaded', 'true');
          if (!result.stored) {
            console.warn('[r2-upload] NocoDB write failed:', result.dbDetail || '(no detail)');
          }
          setStatus(status, 'success', message(config, 'success', hasFile ? 'File uploaded. Sending your form…' : 'Sending your form…'));

          uploading = false;
          form.classList.remove('is-r2-uploading');
          if (submitButton) submitButton.disabled = false;
          form.requestSubmit(submitter || undefined);
        })
        .catch(function (error) {
          var isVerificationError = error && (
            error.code === 'turnstile_failed' ||
            error.code === 'turnstile_mismatch' ||
            error.code === 'turnstile_not_configured'
          );

          /* No attachment: never block the customer — let Shopify's native form
             (email) go through even if the NocoDB write failed. */
          if (!hasFile && !isVerificationError) {
            console.warn('[r2-upload] Worker call failed — submitted by email only, NOT recorded in NocoDB:', error);
            form.setAttribute('data-r2-uploaded', 'true');
            uploading = false;
            form.classList.remove('is-r2-uploading');
            if (submitButton) submitButton.disabled = false;
            form.requestSubmit(submitter || undefined);
            return;
          }

          var fallback = isVerificationError
            ? message(config, 'verification-failed', 'Security verification failed. Please try again.')
            : error && error.name === 'AbortError'
              ? message(config, 'timeout', 'The upload timed out. Please try again.')
              : message(config, 'failed', hasFile ? 'The file could not be uploaded. Please try again.' : 'The form could not be sent. Please try again.');
          setStatus(status, 'error', fallback);
          resetTurnstile(turnstileState);
          uploading = false;
          form.classList.remove('is-r2-uploading');
          if (submitButton) submitButton.disabled = false;
        })
        .finally(function () {
          window.clearTimeout(timer);
        });
    });
  }

  function initAll(scope) {
    if (scope.matches && scope.matches(FORM_SELECTOR)) initForm(scope);
    Array.prototype.forEach.call(scope.querySelectorAll(FORM_SELECTOR), initForm);
  }

  function renderAllTurnstile() {
    Array.prototype.forEach.call(document.querySelectorAll(FORM_SELECTOR), function (form) {
      if (form.ebTurnstileState) renderTurnstile(form.ebTurnstileState);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    Array.prototype.forEach.call(event.target.querySelectorAll(FORM_SELECTOR), function (form) {
      var state = form.ebTurnstileState;
      if (state && window.turnstile && state.widgetId !== null) {
        try { window.turnstile.remove(state.widgetId); } catch (_error) { /* no-op */ }
      }
    });
  });

  document.addEventListener('eb:turnstile-ready', renderAllTurnstile);
})();
